import React, { useState, useEffect, useMemo } from "react";
import { formatCurrency } from "../component/CarCard";
import Icons from "../component/Icons";
import { InvestorLedgerViewModel, type InvestorCard } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import { client } from "../../client";

type InvestorDashboardData = InvestorCard;

const AdminInvestors = () => {
    const vm = useMemo(() => new InvestorLedgerViewModel(client), []);
    const { investors, history } = useViewModel(vm);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newInvestorName, setNewInvestorName] = useState("");

    const [txModalData, setTxModalData] = useState<{ investorId: string, name: string, type: 'DEPOSIT' | 'WITHDRAWAL' | 'EXPENSE' } | null>(null);
    const [selectedInvestor, setSelectedInvestor] = useState<InvestorDashboardData | null>(null);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    const handleSelectInvestor = (inv: InvestorDashboardData) => {
        setSelectedInvestor(inv);
        vm.selectHistory(inv.id);
    };

    const handleAddInvestor = async (e: React.FormEvent) => {
        e.preventDefault();
        if (await vm.create(newInvestorName)) {
            setNewInvestorName("");
            setIsAddModalOpen(false);
        }
    };

    const handleTransaction = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!txModalData) return;
        const formData = new FormData(e.currentTarget);
        const ok = await vm.addTransaction(txModalData.investorId, {
            type: txModalData.type,
            amount: Number(formData.get('amount')),
            date: String(formData.get('date') || ''),
            notes: String(formData.get('notes') || ''),
        });
        if (ok) {
            if (selectedInvestor && selectedInvestor.id === txModalData.investorId) {
                vm.selectHistory(selectedInvestor.id);
            }
            setTxModalData(null);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
                <div>
                    <h1 className="text-4xl font-black italic tracking-tighter uppercase text-gray-900 leading-none">Investor Ledger</h1>
                </div>
                <button 
                    onClick={() => setIsAddModalOpen(true)}
                    className="w-full sm:w-auto bg-gray-900 text-white px-8 py-3 rounded-xl font-black uppercase shadow-lg hover:bg-black transition" 
                >
                    + Add New Investor
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {investors.map(inv => (
                    <div key={inv.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col group hover:shadow-lg transition">
                        <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center cursor-pointer" onClick={() => handleSelectInvestor(inv)}>
                            <h3 className="text-2xl font-black uppercase italic tracking-tighter text-gray-900">{inv.name}</h3>
                            <Icons.Users />
                        </div>
                        <div className="p-6 flex-grow space-y-5">
                            <div>
                                <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Capital Pool</span>
                                <span className="text-3xl font-black text-gray-900 tracking-tighter">{formatCurrency(inv.totalCapital)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Liquid Cash</span>
                                    <span className={`text-lg font-black tracking-tight ${inv.liquid < 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(inv.liquid)}</span>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Active Cars</span>
                                    <span className="text-lg font-black text-amber-600 tracking-tight">{formatCurrency(inv.tiedUp)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {selectedInvestor && (
                <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setSelectedInvestor(null)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <div className="p-8 border-b border-gray-100 flex justify-between items-start bg-gray-50">
                            <div>
                                <h2 className="text-3xl font-black italic uppercase tracking-tighter text-gray-900">{selectedInvestor.name}'s Ledger</h2>
                            </div>
                            <button onClick={() => setSelectedInvestor(null)} className="p-2 bg-gray-200 rounded-full">&times;</button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-1 bg-gray-900 text-white text-center p-1">
                            <div className="py-3">
                                <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400">Total Deposits</span>
                                <span className="text-lg font-black">{formatCurrency(selectedInvestor.deposits)}</span>
                            </div>
                            <div className="py-3 border-x border-gray-700">
                                <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400">Net Profits Received</span>
                                <span className={`text-lg font-black ${selectedInvestor.saleReturns >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(selectedInvestor.saleReturns)}</span>
                            </div>
                            <div className="py-3">
                                <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400">Current Liquid</span>
                                <span className="text-lg font-black text-blue-400">{formatCurrency(selectedInvestor.liquid)}</span>
                            </div>
                        </div>

                        <div className="flex-grow overflow-y-auto bg-white p-6 custom-scrollbar">
                            <div className="mb-8">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-black uppercase tracking-widest text-gray-400 border-b pb-2 flex-grow">Transaction History</h4>
                                    <button onClick={() => setTxModalData({ investorId: selectedInvestor.id, name: selectedInvestor.name, type: 'EXPENSE' })} className="ml-4 text-[10px] font-black uppercase tracking-widest text-red-600 hover:underline">+ Add Manual Expense</button>
                                </div>
                                <div className="space-y-3">
                                    {history.map(tx => (
                                        <div key={tx.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-xl border border-gray-100">
                                            <div>
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded text-white ${tx.type === 'DEPOSIT' ? 'bg-green-600' : tx.type === 'WITHDRAWAL' ? 'bg-amber-600' : tx.type === 'SALE_RETURN' ? 'bg-blue-600' : 'bg-red-600'}`}>{tx.type.replace('_', ' ')}</span>
                                                    <span className="text-xs font-bold text-gray-400">{tx.date}</span>
                                                </div>
                                                <p className="font-bold text-sm text-gray-800">{tx.notes || 'No description provided'}</p>
                                            </div>
                                            <span className={`text-xl font-black tracking-tight ${(tx.type === 'WITHDRAWAL' || tx.type === 'EXPENSE' || tx.amount < 0) ? 'text-red-600' : 'text-green-600'}`}>
                                                {(tx.type === 'WITHDRAWAL' || tx.type === 'EXPENSE') ? '-' : (tx.amount < 0 ? '' : '+')}
                                                {formatCurrency(Math.abs(tx.amount))}
                                            </span>
                                        </div>
                                    ))}
                                    {history.length === 0 && <p className="text-center text-sm font-bold text-gray-400 py-4">No transactions recorded.</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isAddModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsAddModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleAddInvestor} className="p-6">
                            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Investor Name</label>
                            <input required type="text" value={newInvestorName} onChange={e => setNewInvestorName(e.target.value)} className="w-full border-2 border-gray-100 rounded-xl p-3 focus:border-gray-900 outline-none transition font-bold" />
                            <button type="submit" className="w-full mt-6 bg-gray-900 text-white font-black uppercase py-4 rounded-xl shadow-lg">Create Profile</button>
                        </form>
                    </div>
                </div>
            )}

            {txModalData && (
                <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setTxModalData(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleTransaction} className="p-6 space-y-4">
                            <p className="font-bold text-gray-800 uppercase text-xs tracking-widest mb-4">Investor: {txModalData.name}</p>
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Amount ($)</label>
                                <input required name="amount" type="number" step="0.01" min="0" className="w-full border-2 border-gray-100 rounded-xl p-3 outline-none transition font-bold text-lg" />
                            </div>
                            <button type="submit" className="w-full mt-4 bg-gray-900 text-white font-black uppercase py-4 rounded-xl shadow-lg">Confirm Action</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminInvestors;